import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoCapture from "@/components/PhotoCapture";
import { toast } from "sonner";
import {
  Gamepad2, Plus, Trash2, Loader2, Sparkles, Play, ChevronRight, Trophy,
  Users, Clock, CheckCircle2, X, Flag, Crown, ArrowLeft, Image as ImageIcon, Star,
} from "lucide-react";

const OPT_META = [
  { color: "bg-rose-500", label: "A" },
  { color: "bg-sky-500", label: "B" },
  { color: "bg-amber-500", label: "C" },
  { color: "bg-emerald-500", label: "D" },
];
const emptyQ = () => ({ text: "", type: "pg", options: ["", "", "", ""], answer: "0", time_limit: 20, image: "" });

function QImage({ value, onChange, idx, qText, kelas }) {
  const [gen, setGen] = useState(false);
  const aiGen = async () => {
    if (!qText || !qText.trim()) { toast.error("Tulis pertanyaannya dulu ya, baru AI bisa membuat gambar"); return; }
    setGen(true);
    try {
      const r = await api.post("/games/suggest-image", { text: qText, kelas: kelas ? parseInt(kelas) : null });
      onChange(r.data.image);
      toast.success("Ilustrasi AI dibuat! 🎨");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat gambar AI"); }
    finally { setGen(false); }
  };
  if (value) {
    return (
      <div className="relative inline-block">
        <img src={fileUrl(value)} alt="gambar soal" className="h-28 rounded-xl border-2 border-violet-100 object-cover" data-testid={`game-q-${idx}-image`} />
        <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 shadow" data-testid={`game-q-${idx}-image-remove`} aria-label="Hapus gambar"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-2.5">
      <p className="text-xs text-slate-400 mb-1.5 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Gambar soal (opsional)</p>
      <div className="flex flex-wrap items-center gap-2">
        <PhotoCapture category="game" facingMode="environment" label="Tambah Gambar" testIdPrefix={`game-q-${idx}-img`} onUploaded={(p) => onChange(p)} />
        <Button type="button" variant="outline" size="sm" onClick={aiGen} disabled={gen} className="rounded-lg h-9 text-violet-700 border-violet-200 hover:bg-violet-50" data-testid={`game-q-${idx}-ai-image`}>
          {gen ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Gambar AI
        </Button>
      </div>
    </div>
  );
}

export default function GamePanel() {
  const [view, setView] = useState("list");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hostId, setHostId] = useState(null);

  const loadGames = () => { setLoading(true); api.get("/games").then((r) => setGames(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { loadGames(); }, []);

  const openHost = (id) => { setHostId(id); setView("host"); };

  if (view === "create") return <CreateGame onCancel={() => { setView("list"); loadGames(); }} onCreated={openHost} />;
  if (view === "host" && hostId) return <HostGame gameId={hostId} onExit={() => { setHostId(null); setView("list"); loadGames(); }} />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2"><Gamepad2 className="w-7 h-7 text-violet-600" /> Kuis Langsung</h1>
          <p className="text-slate-500 mt-1">Buat room kuis seru, murid gabung pakai PIN, jawab bersamaan & rebut peringkat! 🏆</p>
        </div>
        <Button onClick={() => setView("create")} className="rounded-xl h-11 bg-violet-600 hover:bg-violet-700" data-testid="create-game-btn"><Plus className="w-5 h-5 mr-2" /> Buat Kuis</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>
      ) : games.length === 0 ? (
        <div className="text-center py-20 text-slate-400"><Gamepad2 className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>Belum ada kuis. Buat yang pertama!</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 stagger">
          {games.map((g) => <GameCard key={g.id} g={g} onOpen={() => openHost(g.id)} onDeleted={loadGames} />)}
        </div>
      )}
    </div>
  );
}

function GameCard({ g, onOpen, onDeleted }) {
  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Hapus kuis ini?")) return;
    await api.delete(`/games/${g.id}`); toast.success("Kuis dihapus"); onDeleted();
  };
  const live = g.status !== "ended";
  return (
    <div onClick={onOpen} className="cursor-pointer bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`game-card-${g.id}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${live ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{live ? "Aktif" : "Selesai"}</span>
        <button onClick={del} className="text-rose-500 hover:text-rose-700" data-testid={`delete-game-${g.id}`} aria-label="Hapus kuis"><Trash2 className="w-4 h-4" /></button>
      </div>
      <h3 className="font-display text-lg font-semibold text-slate-800 mt-2">{g.title}</h3>
      <p className="text-sm text-slate-500 mt-1">{g.question_count} soal · {g.player_count} pemain</p>
      <p className="text-xs text-violet-600 font-mono font-bold mt-2 tracking-widest">PIN {g.pin}</p>
    </div>
  );
}

function CreateGame({ onCancel, onCreated }) {
  const [title, setTitle] = useState("");
  const [kelas, setKelas] = useState("");
  const [starsPerCorrect, setStarsPerCorrect] = useState("3");
  const [winnerBonus, setWinnerBonus] = useState("10");
  const [questions, setQuestions] = useState([emptyQ()]);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState("5");
  const [withImages, setWithImages] = useState(false);
  const [genLoading, setGenLoading] = useState(false);

  const updQ = (i, patch) => setQuestions((qs) => qs.map((q, x) => (x === i ? { ...q, ...patch } : q)));
  const updOpt = (i, oi, v) => setQuestions((qs) => qs.map((q, x) => (x === i ? { ...q, options: q.options.map((o, y) => (y === oi ? v : o)) } : q)));
  const addQ = () => setQuestions((qs) => [...qs, emptyQ()]);
  const delQ = (i) => setQuestions((qs) => (qs.length > 1 ? qs.filter((_, x) => x !== i) : qs));

  const genAI = async () => {
    if (!topic.trim()) { toast.error("Isi topik dulu ya"); return; }
    setGenLoading(true);
    try {
      const r = await api.post("/games/generate", { topic, kelas: kelas ? parseInt(kelas) : null, count: parseInt(count) || 5 });
      const qs = (r.data.questions || []).map((q) => ({ text: q.text || "", type: "pg", options: [...(q.options || []), "", "", "", ""].slice(0, 4), answer: String(q.answer ?? "0"), time_limit: q.time_limit || 20, image: "" }));
      if (!qs.length) { toast.error("AI tidak menghasilkan soal"); return; }
      setQuestions(qs); setAiOpen(false);
      toast.success(`${qs.length} soal dibuat AI! Periksa & ubah bila perlu.`);
      if (withImages) {
        toast.info("AI sedang membuat ilustrasi tiap soal... muncul otomatis 🎨");
        qs.forEach((q, i) => {
          api.post("/games/suggest-image", { text: q.text, kelas: kelas ? parseInt(kelas) : null })
            .then((r) => setQuestions((prev) => prev.map((pq, x) => (x === i ? { ...pq, image: r.data.image } : pq))))
            .catch(() => {});
        });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat soal AI"); }
    finally { setGenLoading(false); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Judul kuis wajib diisi"); return; }
    const clean = questions.filter((q) => q.text.trim());
    if (!clean.length) { toast.error("Tambah minimal 1 soal"); return; }
    for (const q of clean) {
      if (q.type === "pg") {
        if (q.options.filter((o) => o.trim()).length < 2) { toast.error("Soal pilihan ganda butuh minimal 2 opsi"); return; }
      } else if (!q.answer.trim()) { toast.error("Isi kunci jawaban soal isian"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        title, kelas: kelas ? parseInt(kelas) : null,
        stars_per_correct: Math.max(0, parseInt(starsPerCorrect) || 0),
        winner_bonus: Math.max(0, parseInt(winnerBonus) || 0),
        questions: clean.map((q) => ({ text: q.text, type: q.type, options: q.type === "pg" ? q.options.filter((o) => o.trim()) : [], answer: q.answer, time_limit: parseInt(q.time_limit) || 20, image: q.image || "" })),
      };
      const r = await api.post("/games", payload);
      toast.success("Kuis dibuat! Bagikan PIN ke murid.");
      onCreated(r.data.id);
    } catch (e) { toast.error("Gagal membuat kuis"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="game-create-back"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <h1 className="font-display text-2xl font-bold text-slate-800">Buat Kuis Langsung</h1>

      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><Label>Judul Kuis</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-xl" placeholder="Contoh: Kuis Perkalian Seru" data-testid="game-title-input" /></div>
          <div>
            <Label>Kelas (opsional)</Label>
            <Select value={kelas} onValueChange={setKelas}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="game-kelas-select"><SelectValue placeholder="Semua" /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-700 mb-2"><Star className="w-4 h-4" /> Hadiah Bintang (masuk ke profil murid)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Bintang per jawaban benar</Label>
              <Input type="number" min="0" value={starsPerCorrect} onChange={(e) => setStarsPerCorrect(e.target.value)} className="mt-1 rounded-xl bg-white" data-testid="game-stars-per-correct" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Bonus bintang untuk Juara #1</Label>
              <Input type="number" min="0" value={winnerBonus} onChange={(e) => setWinnerBonus(e.target.value)} className="mt-1 rounded-xl bg-white" data-testid="game-winner-bonus" />
            </div>
          </div>
          <p className="text-[11px] text-amber-600/80 mt-2">Isi 0 jika tidak ingin memberi bintang. Bintang menambah poin & lencana di profil murid.</p>
        </div>

        <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4">
          {!aiOpen ? (
            <button onClick={() => setAiOpen(true)} className="flex items-center gap-2 text-sm font-semibold text-violet-700" data-testid="game-ai-open"><Sparkles className="w-4 h-4" /> Buat soal otomatis dengan AI</button>
          ) : (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-violet-700"><Sparkles className="w-4 h-4" /> Buat soal dengan AI</p>
              <div className="grid sm:grid-cols-4 gap-2">
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} className="sm:col-span-3 rounded-xl bg-white" placeholder="Topik, cth: penjumlahan, hewan, tata surya" data-testid="game-ai-topic" />
                <Select value={count} onValueChange={setCount}><SelectTrigger className="rounded-xl bg-white" data-testid="game-ai-count"><SelectValue /></SelectTrigger><SelectContent>{[3, 5, 8, 10].map((n) => <SelectItem key={n} value={String(n)}>{n} soal</SelectItem>)}</SelectContent></Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-violet-700 cursor-pointer select-none">
                <input type="checkbox" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} className="w-4 h-4 accent-violet-600 rounded" data-testid="game-ai-with-images" />
                <ImageIcon className="w-4 h-4" /> Sertakan ilustrasi AI untuk tiap soal (lebih lama)
              </label>
              <div className="flex gap-2">
                <Button onClick={genAI} disabled={genLoading} className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="game-ai-generate">{genLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Buatkan</Button>
                <Button variant="outline" onClick={() => setAiOpen(false)} className="rounded-xl">Batal</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm space-y-3" data-testid={`game-q-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-display font-semibold text-slate-700">Soal {i + 1}</span>
              <div className="flex items-center gap-2">
                <Select value={q.type} onValueChange={(v) => updQ(i, { type: v, answer: v === "pg" ? "0" : "" })}>
                  <SelectTrigger className="h-8 rounded-lg w-36 text-xs" data-testid={`game-q-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pg">Pilihan Ganda</SelectItem><SelectItem value="isian">Isian Singkat</SelectItem></SelectContent>
                </Select>
                <Select value={String(q.time_limit)} onValueChange={(v) => updQ(i, { time_limit: v })}>
                  <SelectTrigger className="h-8 rounded-lg w-24 text-xs" data-testid={`game-q-time-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{[10, 15, 20, 30, 45, 60].map((t) => <SelectItem key={t} value={String(t)}>{t} dtk</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => delQ(i)} className="text-rose-500" data-testid={`game-q-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <Input value={q.text} onChange={(e) => updQ(i, { text: e.target.value })} className="rounded-xl" placeholder="Tulis pertanyaan" data-testid={`game-q-text-${i}`} />
            <QImage value={q.image} idx={i} qText={q.text} kelas={kelas} onChange={(v) => updQ(i, { image: v })} />
            {q.type === "pg" ? (
              <div className="grid sm:grid-cols-2 gap-2">
                {q.options.map((o, oi) => (
                  <label key={oi} className={`flex items-center gap-2 rounded-xl p-1 pr-3 border-2 transition-colors ${q.answer === String(oi) ? "border-emerald-400 bg-emerald-50" : "border-slate-100"}`}>
                    <span className={`w-8 h-8 rounded-lg ${OPT_META[oi].color} text-white font-bold flex items-center justify-center shrink-0`}>{OPT_META[oi].label}</span>
                    <Input value={o} onChange={(e) => updOpt(i, oi, e.target.value)} className="border-0 shadow-none focus-visible:ring-0 h-8 px-1" placeholder={`Opsi ${OPT_META[oi].label}`} data-testid={`game-q-${i}-opt-${oi}`} />
                    <input type="radio" name={`correct-${i}`} checked={q.answer === String(oi)} onChange={() => updQ(i, { answer: String(oi) })} className="w-4 h-4 accent-emerald-600" data-testid={`game-q-${i}-correct-${oi}`} title="Tandai jawaban benar" />
                  </label>
                ))}
                <p className="sm:col-span-2 text-[11px] text-slate-400">Centang bulatan hijau di opsi yang benar.</p>
              </div>
            ) : (
              <Input value={q.answer} onChange={(e) => updQ(i, { answer: e.target.value })} className="rounded-xl" placeholder="Kunci jawaban (isian)" data-testid={`game-q-${i}-answer`} />
            )}
          </div>
        ))}
        <Button variant="outline" onClick={addQ} className="rounded-xl w-full" data-testid="game-add-question"><Plus className="w-4 h-4 mr-2" /> Tambah Soal</Button>
      </div>

      <div className="flex gap-2 sticky bottom-4">
        <Button onClick={save} disabled={saving} className="flex-1 h-12 rounded-xl bg-violet-600 hover:bg-violet-700 font-semibold shadow-lg" data-testid="game-save-btn">{saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Play className="w-5 h-5 mr-2" />} Buat & Mulai Room</Button>
      </div>
    </div>
  );
}

function HostGame({ gameId, onExit }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const poll = () => api.get(`/games/${gameId}/host`).then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { poll(); timer.current = setInterval(poll, 1500); return () => clearInterval(timer.current); }, [gameId]); // eslint-disable-line

  const act = async (path) => {
    setBusy(true);
    try { const r = await api.post(`/games/${gameId}/${path}`); setS(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;

  const isLast = s.current_index >= s.total_questions - 1;

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onExit} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="game-host-exit"><ArrowLeft className="w-4 h-4" /> Keluar</button>
        <div className="flex items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 text-violet-700 font-semibold"><Users className="w-4 h-4" /> {s.player_count} pemain</span>
          {s.status === "question" && <span className="inline-flex items-center gap-1.5 text-slate-600"><Clock className="w-4 h-4" /> {s.time_left}s</span>}
        </div>
      </div>

      {s.status === "lobby" && (
        <div className="text-center space-y-5">
          <h1 className="font-display text-2xl font-bold text-slate-800">{s.title}</h1>
          <div className="bg-violet-600 text-white rounded-3xl py-8 px-6 shadow-xl inline-block" data-testid="game-lobby-pin">
            <p className="text-violet-200 text-sm font-medium">PIN Gabung</p>
            <p className="font-mono text-6xl font-black tracking-[0.2em] mt-1">{s.pin}</p>
          </div>
          <p className="text-slate-500">Minta murid buka menu <b>Main Kuis</b> lalu masukkan PIN di atas.</p>
          <div className="flex flex-wrap gap-2 justify-center" data-testid="game-lobby-players">
            {s.players.map((p) => <span key={p.student_id} className="bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm font-medium text-slate-700 animate-pop">{p.name}</span>)}
            {s.players.length === 0 && <span className="text-slate-400 text-sm">Menunggu murid bergabung...</span>}
          </div>
          <Button onClick={() => act("start")} disabled={busy || s.player_count === 0} className="h-12 px-8 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold text-lg shadow-lg" data-testid="game-start-btn"><Play className="w-5 h-5 mr-2" /> Mulai Kuis</Button>
        </div>
      )}

      {(s.status === "question" || s.status === "reveal") && s.question && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Soal {s.question.no} dari {s.total_questions}</span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-violet-700"><CheckCircle2 className="w-4 h-4" /> {s.answered_count}/{s.player_count} menjawab</span>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            {s.question.image && <img src={fileUrl(s.question.image)} alt="gambar soal" className="max-h-64 mx-auto mb-4 rounded-xl object-contain" data-testid="game-host-q-image" />}
            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-800 text-center">{s.question.text}</h2>
          </div>
          {s.question.type === "pg" ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {s.question.options.map((o, oi) => {
                const correct = s.status === "reveal" && String(oi) === String(s.question.answer);
                const dim = s.status === "reveal" && !correct;
                return (
                  <div key={oi} className={`relative rounded-2xl p-4 text-white font-semibold flex items-center gap-3 transition-all ${OPT_META[oi].color} ${dim ? "opacity-40" : ""} ${correct ? "ring-4 ring-emerald-300 scale-[1.02]" : ""}`} data-testid={`game-host-opt-${oi}`}>
                    <span className="w-8 h-8 rounded-lg bg-white/25 flex items-center justify-center font-black">{OPT_META[oi].label}</span>
                    <span className="flex-1">{o}</span>
                    {correct && <CheckCircle2 className="w-6 h-6" />}
                    {s.status === "reveal" && <span className="bg-white/25 rounded-full px-2 py-0.5 text-sm">{s.option_counts[oi] || 0}</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            s.status === "reveal" && <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center text-emerald-800 font-semibold">Jawaban: {s.question.answer}</div>
          )}

          {s.status === "reveal" && (
            <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
              <p className="font-display font-semibold text-slate-700 mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Peringkat</p>
              <Leaderboard players={s.players} />
            </div>
          )}

          <div className="flex gap-2">
            {s.status === "question" ? (
              <Button onClick={() => act("reveal")} disabled={busy} className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-600 font-semibold" data-testid="game-reveal-btn">Tampilkan Jawaban & Peringkat</Button>
            ) : isLast ? (
              <Button onClick={() => act("next")} disabled={busy} className="flex-1 h-12 rounded-xl bg-violet-600 hover:bg-violet-700 font-semibold" data-testid="game-finish-btn"><Flag className="w-5 h-5 mr-2" /> Lihat Juara</Button>
            ) : (
              <Button onClick={() => act("next")} disabled={busy} className="flex-1 h-12 rounded-xl bg-violet-600 hover:bg-violet-700 font-semibold" data-testid="game-next-btn">Soal Berikutnya <ChevronRight className="w-5 h-5 ml-1" /></Button>
            )}
          </div>
        </div>
      )}

      {s.status === "ended" && (
        <div className="space-y-5 text-center">
          <h1 className="font-display text-2xl font-bold text-slate-800 flex items-center justify-center gap-2"><Crown className="w-7 h-7 text-amber-500" /> Juara Kuis!</h1>
          <Podium players={s.players} />
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-left"><Leaderboard players={s.players} /></div>
          <Button onClick={onExit} className="h-11 px-6 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="game-ended-back">Selesai & Kembali</Button>
        </div>
      )}
    </div>
  );
}

function Leaderboard({ players }) {
  if (!players.length) return <p className="text-sm text-slate-400">Belum ada pemain.</p>;
  return (
    <div className="space-y-1.5" data-testid="game-leaderboard">
      {players.slice(0, 10).map((p) => (
        <div key={p.student_id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold ${p.rank === 1 ? "bg-amber-400 text-white" : p.rank === 2 ? "bg-slate-300 text-white" : p.rank === 3 ? "bg-orange-400 text-white" : "bg-slate-200 text-slate-600"}`}>{p.rank}</span>
          <span className="flex-1 font-medium text-slate-700 truncate">{p.name}</span>
          <span className="font-display font-bold text-violet-700">{p.score}</span>
        </div>
      ))}
    </div>
  );
}

function Podium({ players }) {
  const top = players.slice(0, 3);
  const order = [top[1], top[0], top[2]].filter(Boolean);
  const heights = { 0: "h-20", 1: "h-28", 2: "h-16" };
  return (
    <div className="flex items-end justify-center gap-3" data-testid="game-podium">
      {order.map((p) => {
        const h = heights[p.rank - 1] || "h-16";
        return (
          <div key={p.student_id} className="flex flex-col items-center">
            {p.rank === 1 && <Crown className="w-6 h-6 text-amber-500 mb-1" />}
            <span className="text-sm font-semibold text-slate-700 max-w-[90px] truncate">{p.name}</span>
            <span className="text-xs text-violet-600 font-bold">{p.score}</span>
            <div className={`w-20 ${h} rounded-t-xl mt-1 flex items-start justify-center pt-2 text-white font-black text-2xl ${p.rank === 1 ? "bg-amber-400" : p.rank === 2 ? "bg-slate-300" : "bg-orange-400"}`}>{p.rank}</div>
          </div>
        );
      })}
    </div>
  );
}
